import { useState } from "react";

import { useGetSingleListPersonQuery } from "@/pages/Private/redux/list/list.api";

import { ListPerson } from "../../../schema/list";
import { OrganizationProfile } from "../../../schema/organization-profiles";

interface ProfileRetarget {
	/** The lead the panel should render — the colleague once one has been picked. */
	lead?: ListPerson;
	/** Name of the lead to go back to, or `null` when the panel is on its own lead. */
	backToName: string | null;
	selectProfile: (profile: OrganizationProfile) => void;
	clear: () => void;
}

/**
 * Lets the list / Messages detail panel show a colleague from its own Profiles tab (PREC-4116).
 *
 * Unlike {@link ContactLeadPanel}, this panel is driven by a `listPerson` prop owned by the table
 * behind it, and that table has no row for a colleague who is on a different list — so the panel
 * re-targets itself instead. Every profile row *is* a `list_person` (that is where the endpoint
 * reads them from), so one can always be resolved: the membership on the current list if there is
 * one, otherwise the first.
 *
 * The retarget is keyed to the lead it was opened from rather than reset by an effect, so selecting
 * another row in the table drops it during render — there is no window in which the back link points
 * at a lead the panel has already left.
 */
export const useProfileRetarget = ({
	listPerson,
	listId,
}: {
	listPerson?: ListPerson;
	listId?: number;
}): ProfileRetarget => {
	const [retarget, setRetarget] = useState<{ fromId: number; listPersonId: number } | null>(null);
	const activeId = retarget && retarget.fromId === listPerson?.id ? retarget.listPersonId : null;
	const { data: retargetedLead } = useGetSingleListPersonQuery(activeId ?? 0, { skip: !activeId });

	return {
		// Keep the original on screen until the colleague's row arrives — the back link already says
		// the panel is moving, and a blank panel in between reads as a failure.
		lead: activeId ? (retargetedLead ?? listPerson) : listPerson,
		backToName: activeId ? (listPerson?.prospect?.name ?? listPerson?.offer?.name ?? "") : null,
		selectProfile: (profile) => {
			const membership = profile.lists.find((list) => list.listId === listId) ?? profile.lists[0];

			if (!listPerson?.id || !membership) {
				return;
			}

			setRetarget({ fromId: listPerson.id, listPersonId: membership.listPersonId });
		},
		clear: () => setRetarget(null),
	};
};
